import { useCallback, useEffect, useState } from "react";
import Layout from "./shared/Layout";
import { Toaster, toast } from "react-hot-toast";
import DataTable from "react-data-table-component";
import axiosIns, { API_URL } from "../_config";
import { decryptData } from "../_seivices";
import LoadingButton from "./shared/LoadingButton";
import useAsyncSubmit from "./shared/useAsyncSubmit";

const getFaqResponseData = (response) => {
    const encrypted = response?.data?.encryptedData || response?.data?.payload || response?.data?.data;

    if (typeof encrypted === "string") {
        return decryptData(encrypted);
    }

    return response?.data;
};

const Faqs = () => {
    const [isFormopen, setIsFormopen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { isSubmitting, run: runSubmit } = useAsyncSubmit();

    const [formData, setFormData] = useState({
        id: 0,
        category_id: "",
        question: "",
        answer: ""
    });

    // Input Change Handler
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchData = useCallback(async (categoryId = "") => {
        setLoading(true);
        try {
            if (categoryId) {
                const response = await axiosIns.get(`${API_URL.GetFaqsByCategoryId}?category_id=${categoryId}`);
                const decrypted = getFaqResponseData(response);

                if (decrypted?.isSuccess === 1 && Array.isArray(decrypted.data)) {
                    setData(decrypted.data);
                } else {
                    setData([]);
                }
                return;
            }

            const response = await axiosIns.get(API_URL.GetFaqs);
            const decrypted = getFaqResponseData(response);

            // Validate decrypted data
            if (decrypted && decrypted.isSuccess === 1 && Array.isArray(decrypted.data)) {

                setData(decrypted?.data);
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCategoryData = async () => {
        try {
            const response = await axiosIns.get(API_URL.getServices);
            const encrypted = response?.data?.payload;

            if (!encrypted) {
                setCategoryData([]);
                return;
            }

            const decrypted = decryptData(encrypted);

            if (decrypted?.isSuccess === 1 && Array.isArray(decrypted.data)) {
                setCategoryData(decrypted.data);
            } else {
                setCategoryData([]);
            }
        } catch (error) {
            setCategoryData([]);
        }
    };

    useEffect(() => {
        fetchCategoryData();
    }, []);

    useEffect(() => {
        fetchData(selectedCategoryId);
    }, [fetchData, selectedCategoryId]);

    const handleCategoryFilterChange = (e) => {
        setSelectedCategoryId(e.target.value);
        setCurrentPage(1);
    };

    const handleAddFaq = () => {
        setFormData(prev => ({
            ...prev,
            id: 0,
            category_id: selectedCategoryId,
            question: "",
            answer: ""
        }));
        setIsFormopen(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage, page) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(page);
    };

    // Save FAQ (Create/Update)
    const onSave = async (e) => {
        e.preventDefault();
        await runSubmit(async () => {
            try {
                const res = await axiosIns.post(API_URL.SaveFaqs, formData);

                if (res?.data?.isSuccess === 1) {
                    toast.success(res.data.message);
                    fetchData(selectedCategoryId);
                    resetForm();
                    setIsFormopen(false);
                }
            } catch (err) {
                toast.error("Something went wrong!");
            }
        });
    };

    // Edit FAQ
    const onEdit = (item) => {
        setFormData({
            id: item.id,
            category_id: item.category_id ?? "",
            question: item.question,
            answer: item.answer
        });
        setIsFormopen(true);
    };

    // Delete FAQ
    const onDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

        try {
            const res = await axiosIns.get(`${API_URL.DeleteFaqsById}?id=${id}`);

            if (res?.data?.isSuccess === 1) {
                toast.success("Deleted successfully");
                fetchData(selectedCategoryId);
            }
        } catch (error) {
            toast.error("Delete failed");
        }
    };

    // Reset Form
    const resetForm = () => {
        setFormData({
            id: 0,
            category_id: selectedCategoryId,
            question: "",
            answer: ""
        });
    };

    const getCategoryName = (categoryId) => {
        if (!categoryId) return "";

        return categoryData.find(category => Number(category.id) === Number(categoryId))?.title || "";
    };

    // Table Columns
    const columns = [
        {
            name: "SN",
            selector: (row, index) => ((currentPage - 1) * rowsPerPage) + index + 1,
            width: "70px"
        },
        {
            name: "Question",
            selector: (row) => row.question,
            sortable: true
        },
        {
            name: "Service",
            selector: (row) => getCategoryName(row.category_id) || "-",
            sortable: true
        },
        {
            name: "Answer",
            selector: (row) => row.answer,
            sortable: false
        },
        {
            name: 'Actions',
            width: '100px',
            cell: row => (
                <div className='d-flex gap-2'>
                    <button onClick={() => onEdit(row)} className='edit_icon_wepper'> <svg id="Layer_1" viewBox="0 0 512 512" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                        <g transform="matrix(1,0,0,1,0,0)">
                            <path d="m41.3 512c-22.8 0-41.3-18.5-41.3-41.3 0-2.6.2-5.2.7-7.7l27.3-144.8c1.5-8.2 5.5-15.7 11.4-21.5l274-274c14.7-14.7 35.1-22.7 57.7-22.7 8.1 0 16.1 1 24 2.8 25.5 5.9 51.1 20.7 72.2 41.8s36 46.7 41.8 72.2c7.4 31.9.2 61.7-19.8 81.7l-274 274c-5.9 5.9-13.4 9.8-21.5 11.4l-144.8 27.4c-2.5.5-5.1.7-7.7.7zm51.8-93.1 72.9-13.8 182-182-59.1-59.1-182 182zm313.3-254.2 23.1-23.1c.2-5.4-3.8-21.8-20.6-38.5-10.1-10.2-22-17.4-32.4-19.8-1.8-.4-3.7-.7-5.5-.7h-.7l-23 23z" fill="#526484" />
                            <path d="m499.4 119.1c-5.4-23.7-19.4-47.6-39.1-67.4s-43.8-33.7-67.4-39.1c-28.5-6.6-54.9-.4-72.3 17.1l-274 274c-4.5 4.4-7.5 10.1-8.7 16.3l-27.4 144.9c-3.2 17 8 33.4 24.9 36.6 1.9.4 3.9.5 5.8.5s3.9-.2 5.8-.5l145-27.4c6.2-1.2 11.9-4.2 16.3-8.6l274-274c17.5-17.5 23.7-43.9 17.1-72.4zm-328.5 295.3-90.3 17 17-90.3 191.3-191.3 73.3 73.3zm267.2-267.2-31.7 31.7-73.2-73.3 31.7-31.7c1-1 5.6-2.3 14-.4 12.2 2.8 25.8 11 37.2 22.4 23.5 23.7 25.6 47.7 22 51.3z" fill="url(#gradient1)" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#526484" />
                                    <stop offset="100%" stopColor="#0063ff" />
                                </linearGradient>
                            </defs>
                        </g>
                    </svg></button>
                    <button onClick={() => onDelete(row.id)} className='edit_icon_wepper'> <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 24 24">
                        <path d="M 10 2 L 9 3 L 4 3 L 4 5 L 5 5 L 5 20 C 5 20.522222 5.1913289 21.05461 5.5683594 21.431641 C 5.9453899 21.808671 6.4777778 22 7 22 L 17 22 C 17.522222 22 18.05461 21.808671 18.431641 21.431641 C 18.808671 21.05461 19 20.522222 19 20 L 19 5 L 20 5 L 20 3 L 15 3 L 14 2 L 10 2 z M 7 5 L 17 5 L 17 20 L 7 20 L 7 5 z M 9 7 L 9 18 L 11 18 L 11 7 L 9 7 z M 13 7 L 13 18 L 15 18 L 15 7 L 13 7 z" fill="#526484"></path>
                    </svg></button>
                </div>
            )
        }
    ];

    return (
        <Layout>
            <div className="px-4">
                <div className="admin-page mt-3 d-sm-flex justify-content-between align-items-center">
                    <div className="admin-heading d-flex" style={{ gap: "20px" }}>
                        <h4 className="mb-0">Faqs</h4>
                        <select
                            className="form-select faq-category-filter"
                            value={selectedCategoryId}
                            onChange={handleCategoryFilterChange}
                        >
                            <option value="">All</option>
                            {categoryData?.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="add_btn">
                        <button onClick={handleAddFaq}>
                            Add Faqs
                        </button>
                    </div>
                </div>
            </div>

            <div className="datatable mt-3">
                <DataTable
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 220px)"
                    columns={columns}
                    data={data || []}
                    progressPending={loading}
                    progressComponent={<p>Loading...</p>}
                    pagination
                    paginationPerPage={rowsPerPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    highlightOnHover
                />
            </div>

            {isFormopen && <div className="backdrop" onClick={() => setIsFormopen(false)}></div>}
            <div id="entryformContainer" className={`adminform entryform ${isFormopen ? 'open' : ''}`}>
                <div className="closebtn">
                    <button className="" onClick={() => setIsFormopen(false)} >
                        <svg clipRule="evenodd" fillRule="evenodd" height="30" imageRendering="optimizeQuality" shapeRendering="geometricPrecision" textRendering="geometricPrecision" viewBox="0 0 6.82666 6.82666" width="30" xmlns="http://www.w3.org/2000/svg" version="1.1" xmlnsXlink="http://www.w3.org/1999/xlink" >
                            <g transform="matrix(1,0,0,1,0,0)">
                                <g id="Layer_x0020_1">
                                    <rect fill="#ffffff00" height="6.827" rx="1.507" width="6.827" stroke="none" />
                                    <path d="m2.21002 1.41333-.79669.79669 1.20331 1.20331-1.20331 1.20331.79669.79669 1.20331-1.20331 1.20332 1.20331.79668-.79669-1.2033-1.20331 1.2033-1.20331-.79669-.79669-1.20331 1.2033z" fill="#fffffe" stroke="none" />
                                </g>
                            </g>
                        </svg>
                    </button>
                </div>

                <div className="enForm">
                    <div className="text-center form-heading">
                        <h4 className="text-center form-heading">
                            {formData.id ? "Edit Faqs" : "Add Faqs"}
                        </h4>
                    </div>

                    <form onSubmit={onSave} className="p-4">

                        <div className="form-group">
                            <label>Services</label>
                            <select
                                name="category_id"
                                className="form-control"
                                value={formData.category_id}
                                onChange={handleChange}
                            >
                                <option value="">Select Service</option>
                                {categoryData?.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Question</label>
                            <input
                                type="text"
                                name="question"
                                className="form-control"
                                value={formData.question}
                                onChange={handleChange}
                                required
                                placeholder="Enter Question"
                            />
                        </div>

                        <div className="form-group">
                            <label>Answer</label>
                            <textarea
                                name="answer"
                                className="form-control"
                                value={formData.answer}
                                onChange={handleChange}
                                required
                                placeholder="Enter Answer"
                            ></textarea>
                        </div>

                        <div className="d-flex form-buttons" style={{ gap: "15px" }}>
                            <div className="submit-btn pt-2">
                                <LoadingButton type="submit" loading={isSubmitting} loadingText={formData?.id ? 'Updating...' : 'Saving...'}>{formData?.id ? 'Update' : 'Submit'}</LoadingButton>
                            </div>
                            <div className="reset-btn pt-2">
                                <button type="button" onClick={resetForm}>Reset</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <Toaster position="top-right" />
        </Layout>

    );

};

export default Faqs;
